import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
type RetriableAxiosConfig = AxiosRequestConfig & { sent?: boolean }
import { getAccessToken, handleRefreshSession } from '../utils/cognito'
import { handleRefreshToken } from '../services/authService'

let apiUrl = import.meta.env.PROD
  ? import.meta.env.VITE_APP_SPINE_API_URL_PRODUCTION
  : import.meta.env.VITE_APP_SPINE_API_URL_DEVELOPMENT

export default function createAPIClient(path = 'v1'): AxiosInstance {
  if (apiUrl === undefined) {
    throw Error('Missing API URL configuration.')
  }

  // Remove the trailing slash.
  apiUrl = apiUrl.replace(/\/$/, '')

  // Remove the leading slash.
  path = path.replace(/^\//, '')

  // Remove the trailing slash.
  path = path.replace(/\/$/, '')

  const client: AxiosInstance = axios.create({
    baseURL: `${apiUrl}/${path}/`,
    headers: {
      'x-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json;charset=utf-8'
    }
  })

  client.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      if (import.meta.env.VITE_APP_AUTH_PROVIDER === 'cognito') {
        const accessToken: string | null = getAccessToken()

        if (accessToken) {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${accessToken}`
          }
        }
      } else if (import.meta.env.VITE_APP_AUTH_PROVIDER === 'jwt') {
        const accessToken: string | null = window.localStorage.getItem('accessToken')

        const temporaryToken: string | null = window.localStorage.getItem('temporaryToken')

        if (accessToken || temporaryToken) {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${accessToken ?? temporaryToken}`
          }
        }
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const config = (error?.config || {}) as RetriableAxiosConfig

      if (error?.response?.status === 401 && !config?.sent) {
        config.sent = true

        if (import.meta.env.VITE_APP_AUTH_PROVIDER === 'cognito') {
          await handleRefreshSession()
        } else if (import.meta.env.VITE_APP_AUTH_PROVIDER === 'jwt') {
          await handleRefreshToken()
        }

        return await client(config)
      }
      return Promise.reject(error)
    }
  )

  return client
}
